import { map } from "rxjs/operators";
import { RateItem } from "./rate-item";
import { CascadeItem } from "./cascade-item";
import { DealItemStateRates, GridColumn, RateRowDefaults } from ".";
import { blendedgroupItem } from "./blended-group";

/**
 * Manages the state of rate items for all deal items, including cascading dropdowns and row defaults.
 */
export class DealStateRates {

  // ---------------- Properties  --------------------

  /**
   * A map of deal item IDs to their corresponding state objects.
   */
  public itemStates = new Map<string, DealItemStateRates>();

  /**
   * A map of header configs to the handsontable.
   */
  public headerConfigs: Map<string, GridColumn[]> = new Map();

  /**
  * list of blended custom role .
  */
  protected blendedGroupList: blendedgroupItem[] = [];

  /**
   * A map of rate IDs to their cascading validation items, used for cascading dropdowns.
   */
  public cascadeRateIdValidations: Map<string, Map<string, CascadeItem>> = new Map();

  /**
   * The sorted list of column indexes that are cascading dropdowns for each tower. Uses colOrder to determine the order of the cascades - left to right.
   */
  public colsCascade: Map<string, number[]> = new Map();

  /**
   * The column index for the description column.
   */
  public colDesc: number = 0;

  /**
   * The column index for the country column.
   */
  public colCountry: number = 0;

  /**
   * The column index for the first calculated column, if any.
   */
  public colFirstCalc?: number | null = null;

  /**
   * A map of rate IDs to their row defaults, used for initializing or resetting rate rows.
   */
  public rowDefaults: Map<string, Map<string, RateRowDefaults>> = new Map();


  // ---------------- Methods  --------------------

  /**
   * Create a new state item for the given deal item id.
   * If the item already exists, it will return the existing item.
   */
  public add(dealItemId: string, state: DealItemStateRates | null = null): DealItemStateRates | undefined {

    if (!dealItemId || dealItemId === '')
      return undefined;

    let item = this.itemStates.get(dealItemId);

    if (!item) {

      if (state) {
        this.itemStates.set(dealItemId, state);
        item = state;
      }
      else {
        item = new DealItemStateRates();
        item.dealItemId = dealItemId;
        item.dataItems = [];
        this.itemStates.set(dealItemId, item);
      }
    }

    return item;
  }

  /**
   * returns true if the deal item state exists in our collection
   */
  public exists(dealItemId: string): boolean {

    if (!dealItemId || dealItemId === '')
      return false;

    return this.itemStates.has(dealItemId);
  }

  /**
   * Get the state data for the given deal item id
   */
  public get(dealItemId?: string | null): DealItemStateRates | undefined {

    if (!dealItemId || dealItemId === '')
      return undefined;

    return this.itemStates.get(dealItemId);
  }

  /**
   * Get the rate items for the given deal item id with any empty or null items removed
   */
  public getData(dealItemId?: string | null): RateItem[] | undefined {

    if (!dealItemId || dealItemId === '')
      return undefined;

    let data = this.itemStates.get(dealItemId)?.dataItems;
    if (!data)
      return [];

    for (let i = data.length; i >= 0; i--) {
      let item = data[i];

      const isNewRow = !item?.id;
      const hasNoFirstIndex = !item?.idxVal1;

      if (!item
        || (isNewRow && hasNoFirstIndex)) {
        data.splice(i, 1);
      }

    }

    return data;
  }

  /**
   * Remove the state data for the given deal item id
   */
  public remove(dealItemId: string): void {
    const index = this.itemStates.delete(dealItemId);
  }

  /**
   * Mark all the rate items in our cache as live and no longer having edits
   */
  public markAsLive(): void {

    this.itemStates.forEach(itemState => {

      itemState.dataItems.forEach(item => {
        item.isEditItem = false;
      });
    });
  }

  /**
   * Reset state tracking, edits, deletions, keeping the data items
   */
  public reset(): void {
    this.itemStates.forEach(itemState => {
      itemState.reset();
    });
  }

  /**
   * Transforms the json data into a map of cascade items for creating and validating cascading dropdowns
   */
  public setCascadeItemsJson(rateId?: string | null, jsonData?: string | null): void {

    if (!rateId || rateId === '' || !jsonData || jsonData === '')
      return;

    const cascades: Map<string, CascadeItem> = CascadeItem.createMapFromJson(jsonData);

    if (this.cascadeRateIdValidations.has(rateId) == false)
      this.cascadeRateIdValidations.set(rateId, cascades);

  }
  
  /**
   * Check if we have cascade items for the given rate id
   */
  public hasCascadeItems(rateId?: string | null): boolean {

    if (!rateId || rateId === '')
      return false;

    return this.cascadeRateIdValidations.has(rateId);
  }

  /**
   * Set headerconfig for each tower
   */
  public setHeaderConfig(accessorId?: string | null, gridCol?: GridColumn[] | null): void {

    if (!accessorId || accessorId === '' || !gridCol || gridCol === null)
      return;

    if (this.headerConfigs.has(accessorId) == false)
      this.headerConfigs.set(accessorId, gridCol);

  }

  /**
   * Get header config for the given id (dealitemid & rateid)
   */
  public getHeaderConfig(accessorId?: string | null): GridColumn[] |null {

    if (!accessorId || accessorId === '')
      return null;

    let hConfig: (GridColumn[] | undefined | null) = this.headerConfigs.get(accessorId);

    if (hConfig)
      return hConfig;

    return null;
  }
  /**
   * Check if we have header config for the given id (dealitemid & rateid)
   */
  public hasHeaderConfig(accessorId?: string | null): boolean {

    if (!accessorId || accessorId === '')
      return false;

    return this.headerConfigs.has(accessorId);
  }

  /**
   * Set ColsCascade for each tower
   */
  public setColsCascade(rateId?: string | null, gridCol?: number[] | null): void {

    if (!rateId || rateId === '' || !gridCol || gridCol === null)
      return;


    if (this.colsCascade.has(rateId) == false)
      this.colsCascade.set(rateId, gridCol);

  }
  /**
   * Get ColsCascade for the given rate id
   */
  public getColsCascade(rateId?: string | null): number[] | null {

    if (!rateId || rateId === '')
      return null;

    let colCascadeConfig: (number[] | undefined | null) = this.colsCascade.get(rateId)
    if (colCascadeConfig)
      return colCascadeConfig;

    return null;
  }
  /**
   * Check if we have ColsCascade data for the given rate id
   */
  public hasColsCascade(rateId?: string | null): boolean {

    if (!rateId || rateId === '')
      return false;

    return this.colsCascade.has(rateId);
  }
  /**
   * Check if we have loaded the rate row defaults for the given rate id
   */
  public hasRateRowDefaults(rateId?: string | null): boolean {

    if (!rateId || rateId === '')
      return false;

    return this.rowDefaults.has(rateId);
  }

  /**
   * Assign the collection of row defaults to our store to manage
   * @param rateId
   * @param data
   * @returns
   */
  public setRateRowDefaults(rateId?: string | null, data?: RateRowDefaults[] | null): void {
    if (!rateId || rateId === '')
      return;

    if (this.rowDefaults.has(rateId) == false) {

      let lookup = new Map<string, RateRowDefaults>();

      if (data) {
        lookup = data.reduce((map, obj) => {
          map.set(obj.description ?? '', obj);
          return map;
        }, new Map<string, RateRowDefaults>());
      }

      this.rowDefaults.set(rateId, lookup);
    }
  }

  /**
  * Set blendd group list
  */
  public setBlendedGroupList(blendedgroups?: blendedgroupItem[] | null): void {

    if (blendedgroups) {
      this.blendedGroupList = blendedgroups;
    }

  }
  /**
   * Get BlendedGroupList
   */
  public getBlendedGroupList(): blendedgroupItem[] | null {

    if (this.blendedGroupList) {
      return this.blendedGroupList;
    }

    return null;
  }
  


  /**
   * Process and extract constant values from the column configurations
   * to facilitate fast lookups of common columns without having to search the array
   * @param items
   */
  public initFromColumnConfigs(items: GridColumn[]) {

    this.colDesc = 0;
    this.colCountry = 0;
    this.colFirstCalc = null;

    for (let pos = 0; pos < items.length; pos++) {

      let item: GridColumn = items[pos];

      switch (item.code) {

        case 'desc':
          this.colDesc = item.colOrder ?? 0;
          break;
        case 'delivered from':
          this.colCountry = item.colOrder ?? 0;
          break;
        case 'low':
          if (!this.colFirstCalc)
            this.colFirstCalc = item.colOrder;
          break;
        default:
          break;
      }
    }
  }
}

